import { ApiService } from '../api/services/ApiService';
import type { AppInfoBody } from '../api/types/app.types';
import { ServiceRegistry } from './ServiceRegistry';

class AppInfoState {
    public microsoftAvailable: boolean = $state(false);
    public microsoftOrigin: string | null = $state(null);
}

/**
 * The static information of the installation (`GET /appInfo`): loaded once at start and again every 10 minutes —
 * not with the polled `/whoAmI` (UserService).
 */
class AppInfoServiceClass {
    private readonly ReloadIntervalMs: number = 10 * 60 * 1000;

    private readonly State: AppInfoState = new AppInfoState();
    private LoadedAt: number = 0;
    private IsStopped: boolean = false;
    private readonly ReloadTimer: ReturnType<typeof setInterval>;
    private readonly HandleVisibility = (): void => {
        if (!document.hidden && Date.now() - this.LoadedAt >= this.ReloadIntervalMs) {
            void this.load();
        }
    };

    public constructor() {
        void this.load();
        this.ReloadTimer = setInterval((): void => {
            void this.load();
        }, this.ReloadIntervalMs);
        document.addEventListener('visibilitychange', this.HandleVisibility);
    }

    /** The only browser origin the Microsoft sign-on works at (development), null when not restricted. */
    public get microsoftOrigin(): string | null {
        return this.State.microsoftOrigin;
    }

    /** Configured and — in development — opened at the required origin. */
    public get isMicrosoftAvailable(): boolean {
        const Origin = this.State.microsoftOrigin;
        return this.State.microsoftAvailable && (Origin === null || Origin === window.location.origin);
    }

    /** Ends reloading for good — the replaced instance after a Vite HMR update (`ServiceRegistry`). */
    public stop(): void {
        this.IsStopped = true;
        clearInterval(this.ReloadTimer);
        document.removeEventListener('visibilitychange', this.HandleVisibility);
    }

    private async load(): Promise<void> {
        const Result = await ApiService.app.getAppInfo();
        const Body: AppInfoBody | undefined = Result.body;
        if (this.IsStopped || !Result.isOk || Body === undefined) {
            return;
        }

        // -
        this.LoadedAt = Date.now();
        this.State.microsoftAvailable = Body.microsoft.available;
        this.State.microsoftOrigin = Body.microsoft.origin;
    }
}

export const AppInfoService = ServiceRegistry.register('AppInfoService', new AppInfoServiceClass());
